import Label from "@/v2-ui/label"
import Skeleton from "@/v2-ui/skeleton"
import cn from "@/v2-ui/utils/utils.cn"
import Button from "@/v2-backoffice-client/components/button"
import FeatureNotice from "@/v2-backoffice-client/feature/FeatureNotice"
import FeatureCountryOption from "@/v2-backoffice-client/feature/country/FeatureCountryOption"
import { useCountryQuery } from "@/v2-backoffice-client/country/country.query"

export type LayerCreateCountriesProps = {
  className?: string
  selected: ReadonlySet<number>
  onChange(selected: ReadonlySet<number>): void
  /** True once the operator tried to create with none ticked. */
  isInvalid: boolean
}

/**
 * Where the new layer is offered. Required, and said so loudly: the console
 * lists a layer only in the countries it has a row for, so a layer created
 * with none is hidden from every customer while everything else about it
 * (published, filed, translated) looks fine — the exact way a layer went
 * missing on 2026-09-08. Nothing is pre-ticked so the choice is a decision,
 * with "Select all" for a layer that belongs everywhere.
 */
function LayerCreateCountries(props: LayerCreateCountriesProps) {
  const { selected, onChange, isInvalid, className } = props

  const { data, loading } = useCountryQuery()
  const countries = data?.getCountries ?? []

  const isEmpty = selected.size === 0
  const isAllSelected =
    countries.length > 0 &&
    countries.every((country) => selected.has(country.Code))

  function toggle(code: number, isChecked: boolean) {
    const next = new Set(selected)
    if (isChecked) next.add(code)
    else next.delete(code)
    onChange(next)
  }

  return (
    <div className={cn("flex shrink-0 flex-col gap-1.5", className)}>
      <span className="flex items-center gap-2">
        <Label>
          Countries <span className="text-destructive">*</span>
        </Label>
        <Button
          size="tiny"
          variant="ghost"
          disabled={loading || isAllSelected}
          data-feature-code="UI_BackofficeLayers_Create_CountriesAll"
          onClick={() =>
            onChange(new Set(countries.map((country) => country.Code)))
          }
        >
          Select all
        </Button>
        {!isEmpty && (
          <Button
            size="tiny"
            variant="ghost"
            data-feature-code="UI_BackofficeLayers_Create_CountriesNone"
            onClick={() => onChange(new Set())}
          >
            None
          </Button>
        )}
      </span>

      {loading ? (
        <div className="flex gap-4" aria-busy="true">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-24" />
        </div>
      ) : (
        <div className="flex flex-wrap gap-x-5 gap-y-1.5">
          {countries.map((country) => (
            <FeatureCountryOption
              key={country.Code}
              code={country.Code}
              alpha2={country.Alpha2}
              name={country.Name}
              isChecked={selected.has(country.Code)}
              isExcluded={false}
              onCheckedChange={(isChecked) => toggle(country.Code, isChecked)}
            />
          ))}
        </div>
      )}

      {isEmpty ? (
        <FeatureNotice tone={isInvalid ? "destructive" : "warning"}>
          <strong>Pick at least one country.</strong> Customers see a layer in
          its ticked countries only — with none ticked it is hidden from
          everyone, however it is published. Select all to offer it everywhere.
        </FeatureNotice>
      ) : (
        <p className="text-[11px] text-muted-foreground">
          Offered in the ticked countries only. Changeable later on the
          layer&apos;s Availability tab.
        </p>
      )}
    </div>
  )
}

export default LayerCreateCountries
